/**
 * New-article companion to `RelatedArticlesEditor`.
 *
 * Same picker UX as the saved-article editor (Autocomplete sourced
 * from the article index, click to add, X to remove) but reads/writes
 * from the editor's reducer state. Sent with the create POST as
 * `related_slugs: [...]`.
 *
 * The article index isn't filtered against a "current article" here —
 * there is no current article on the new-article page. Already-added
 * slugs are still filtered out so the picker doesn't show duplicates.
 */

import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  PendingRelated,
  UseArticleEditor,
} from "./useArticleEditor";
import {
  ResearchArticleIndexItem,
} from "../../../../models/Research";
import { useResearchLayoutContext } from "../ResearchLayout";

export interface PendingRelatedListProps {
  related: PendingRelated[];
  editor: UseArticleEditor;
}

export default function PendingRelatedList({
  related,
  editor,
}: PendingRelatedListProps) {
  const { articles: indexArticles } = useResearchLayoutContext();
  const [picker, setPicker] =
    useState<ResearchArticleIndexItem | null>(null);

  const pickerOptions = useMemo(() => {
    const linkedSlugs = new Set(related.map((r) => r.slug));
    return indexArticles.filter((a) => !linkedSlugs.has(a.slug));
  }, [indexArticles, related]);

  const handleAdd = (target: ResearchArticleIndexItem | null) => {
    if (!target) return;
    editor.pendingRelatedAdd(target);
    setPicker(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fafafa",
        border: "1px solid #e6e6e6",
        borderRadius: "4px",
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: "text.secondary",
          mb: 1.5,
        }}
      >
        Related articles
      </Typography>

      <Autocomplete
        size="small"
        value={picker}
        onChange={(_e, v) => handleAdd(v)}
        options={pickerOptions}
        getOptionLabel={(o) => o.title}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "0.9rem", fontWeight: 600 }}
              >
                {option.title}
              </Typography>
              {option.category && (
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  {option.category.name}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              pickerOptions.length === 0
                ? "No other articles available"
                : "Link a related article…"
            }
            sx={{
              "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
            }}
          />
        )}
        sx={{ mb: 1.5 }}
      />

      {related.length === 0 ? (
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          No related articles staged yet. Add one above — it'll be linked
          when you save the article.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {related.map((r) => (
            <Box
              key={r.slug}
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #e6e6e6",
                borderRadius: "3px",
                px: 1.25,
                py: 0.75,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {r.title}
                  </Typography>
                  {r.category && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      {r.category.name}
                    </Typography>
                  )}
                </Box>
                <Tooltip title="Remove" arrow>
                  <IconButton
                    size="small"
                    onClick={() => editor.pendingRelatedRemove(r.slug)}
                    sx={{ color: "#CF0A2C" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
